import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { H, Table } from "@/admin/AdminPages";

const ROLE_LABEL = { admin: "Amministratore", moderator: "Moderatore", operator: "Operatore" };

const PermPicker = ({ role, value, onChange, meta }) => {
  if (role === "admin") return <p className="rounded-xl bg-[#EFE9FB] px-3 py-2 text-xs text-[#3B1272]">L'amministratore ha accesso a tutte le sezioni, inclusa la gestione degli utenti staff.</p>;
  return (
    <div className="grid grid-cols-2 gap-1.5" data-testid="perm-picker">
      {Object.entries(meta.permissions).map(([k, label]) => (
        <label key={k} className="flex items-center gap-2 rounded-lg border border-[#EEF0F6] px-2.5 py-1.5 text-xs font-semibold">
          <input type="checkbox" checked={value.includes(k)} onChange={(e) => onChange(e.target.checked ? [...value, k] : value.filter((x) => x !== k))} data-testid={`perm-${k}`} />{label}
        </label>
      ))}
    </div>
  );
};

const EMPTY = { username: "", email: "", name: "", password: "", role: "operator" };

const CreateForm = ({ meta, onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [perms, setPerms] = useState(meta.defaults.operator);
  const setRole = (role) => { setF({ ...f, role }); setPerms(meta.defaults[role] || []); };
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/staff", { ...f, username: f.username.toLowerCase(), permissions: perms }); setF(EMPTY); setPerms(meta.defaults.operator); onDone(); toast.success("Utente backend creato"); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const inp = (k, extra = {}) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), className: "field", required: true, "data-testid": `staff-${k}-input`, ...extra });
  return (
    <form onSubmit={submit} className="card-soft h-fit space-y-3 p-5" data-testid="staff-create-form">
      <p className="font-display font-bold">Crea utente backend</p>
      <input placeholder="Nome completo" {...inp("name")} />
      <input placeholder="Username (minuscolo)" pattern="[a-z0-9._\-]+" {...inp("username")} />
      <input type="email" placeholder="Email" {...inp("email")} />
      <input type="password" placeholder="Password (min. 10 caratteri)" minLength={10} autoComplete="new-password" {...inp("password")} />
      <select {...inp("role")} onChange={(e) => setRole(e.target.value)}>{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
      <PermPicker role={f.role} value={perms} onChange={setPerms} meta={meta} />
      <button className="btn-violet w-full" data-testid="staff-create-btn">Crea utente</button>
    </form>
  );
};

const EditDialog = ({ u, meta, onClose, onDone }) => {
  const [role, setRole] = useState(u.role);
  const [perms, setPerms] = useState(u.staff_permissions || []);
  const [status, setStatus] = useState(u.status);
  const [password, setPassword] = useState("");
  const save = async () => {
    try { await api.patch(`/admin/staff/${u.user_id}`, { role, permissions: perms, status, password: password || undefined }); toast.success("Salvato"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-white sm:rounded-2xl" data-testid="staff-edit-dialog">
        <DialogHeader><DialogTitle>{u.name} · {u.username}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <select className="field" value={role} onChange={(e) => { setRole(e.target.value); if (e.target.value !== "admin") setPerms(meta.defaults[e.target.value] || []); }} data-testid="staff-edit-role">{Object.entries(ROLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
          <select className="field" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="staff-edit-status"><option value="active">Attivo</option><option value="suspended">Sospeso</option></select>
        </div>
        <PermPicker role={role} value={perms} onChange={setPerms} meta={meta} />
        <input type="password" className="field" placeholder="Nuova password (facoltativa)" minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="staff-edit-password" />
        <button onClick={save} className="btn-violet" data-testid="staff-edit-save">Salva modifiche</button>
      </DialogContent>
    </Dialog>
  );
};

export default function StaffPage({ me }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-staff"], queryFn: async () => (await api.get("/admin/staff")).data });
  const { data: meta } = useQuery({ queryKey: ["admin-perms"], queryFn: async () => (await api.get("/admin/permissions")).data });
  const [edit, setEdit] = useState(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-staff"] });
  if (!meta) return null;
  return (
    <div data-testid="admin-staff">
      <H title="Utenti backend" sub="Solo gli amministratori creano e modificano gli utenti del pannello. Le modifiche valgono subito (l'utente deve rientrare)." />
      <div className="grid gap-6 xl:grid-cols-[1fr_400px]">
        <Table head={["Nome", "Username", "Ruolo", "Permessi", "Stato", ""]} testid="admin-staff-table">
          {data.map((u) => (
            <tr key={u.user_id} data-testid={`staff-row-${u.user_id}`}>
              <td className="px-4 py-3"><p className="font-semibold">{u.name}</p><p className="text-xs text-[#64748B]">{u.email}</p></td>
              <td className="px-4 py-3">{u.username}</td><td className="px-4 py-3">{ROLE_LABEL[u.role]}</td>
              <td className="max-w-xs px-4 py-3 text-xs text-[#4B4763]">{u.role === "admin" ? "Tutte" : (u.staff_permissions || []).map((p) => meta.permissions[p]).join(", ") || "Nessuna"}</td>
              <td className="px-4 py-3"><span className={`pill ${u.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{u.status === "active" ? "Attivo" : "Sospeso"}</span></td>
              <td className="px-4 py-3">{u.user_id !== me && <button onClick={() => setEdit(u)} className="rounded-full p-1.5 hover:bg-[#EFE9FB]" data-testid={`staff-edit-${u.user_id}`}><Pencil className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
        </Table>
        <CreateForm meta={meta} onDone={refresh} />
      </div>
      {edit && <EditDialog u={edit} meta={meta} onClose={() => setEdit(null)} onDone={refresh} />}
    </div>
  );
}
